import { CourseOption, CourseSummary, CourseUserState } from "../types/course.response";
import { ProgramSummaryResponse } from "../types/program.response";
import {
    NodeFulfillment,
    ProgramFulfillmentResponse,
} from "../types/program-fulfillment.response";
import {
    CourseSet,
    CourseSetSummary,
    Requirement,
    Select,
    SelectSummary,
} from "../types/requirement.response";
import { getBackendCourseKey, getCourseKey } from "./courseUtils";
import { toCourseSummary } from "./programData";

/** 契约 §3.2-3:userStates 中缺失的 key,userState 重置为初始态 */
export const INITIAL_USER_STATE: CourseUserState = {
    status: "NOT_ON_SCHEDULE",
    isScheduled: false,
    isSaved: false,
    creditsReceived: null,
    semester: null,
    sectionNumbers: [],
    requirementBindings: [],
    examScore: null,
    examScoreAlpha: null,
};

/**
 * 把轻量 fulfillment 响应 patch 进客户端缓存的 ProgramSummaryResponse(契约 §3.3 合并规则)。
 * 返回新的 ProgramSummaryResponse;structureVersion 不一致或节点对不上(结构漂移)返回 null,
 * 调用方应全量重拉重接口。
 *
 * @param userItems 最新的用户课程 + 考试(GET /user-courses + /user-exams),
 *   用于回填 courses 字典的 userItemId —— 它在 CourseOption 顶层而非 CourseUserState 内,
 *   轻量响应不携带。
 */
export function applyFulfillmentToProgram(
    program: ProgramSummaryResponse,
    fulfillment: ProgramFulfillmentResponse,
    userItems: CourseOption[],
): ProgramSummaryResponse | null {
    if (program.structureVersion !== fulfillment.structureVersion) {
        console.warn(
            `[fulfillmentMerge] structureVersion mismatch (program=${program.structureVersion}, ` +
            `fulfillment=${fulfillment.structureVersion}), falling back to full reload`,
        );
        return null;
    }

    const requirements: Requirement[] = [];
    for (const requirement of program.requirements) {
        const rootNode = patchNode(requirement.rootNode, fulfillment.nodes);
        if (!rootNode) {
            console.warn(
                `[fulfillmentMerge] node missing in fulfillment.nodes ` +
                `(requirement=${requirement.info.id}), falling back to full reload`,
            );
            return null;
        }
        requirements.push({ ...requirement, rootNode });
    }

    const userItemIdByKey = new Map<string, number>();
    for (const item of userItems) {
        if (item.userItemId != null) {
            userItemIdByKey.set(getCourseKey(item), item.userItemId);
        }
    }

    const courses: Record<string, CourseSummary> = {};
    for (const [key, course] of Object.entries(program.courses)) {
        courses[key] = {
            ...course,
            userState: fulfillment.userStates[key] ?? INITIAL_USER_STATE,
            userItemId: userItemIdByKey.get(getCourseKey(course)) ?? null,
        };
    }

    // 内涵(broad)节点本次改课新增的成员不在缓存的 courses map 里(切片是改课前拉的),
    // 用最新 userItems 合成摘要补齐,"你的课"段才不缺行
    const userItemByBackendKey = new Map<string, CourseOption>();
    for (const item of userItems) {
        userItemByBackendKey.set(getBackendCourseKey(item), item);
    }
    for (const requirement of requirements) {
        for (const key of collectIntensionalMemberKeys(requirement.rootNode)) {
            if (courses[key]) continue;
            const item = userItemByBackendKey.get(key);
            if (item) courses[key] = toCourseSummary(item, key);
        }
    }

    return { ...program, summary: fulfillment.summary, requirements, courses, poolResults: fulfillment.poolResults };
}

function collectIntensionalMemberKeys(node: Select | CourseSet): string[] {
    if (node.type === "COURSE_SET") {
        return node.query != null ? node.requiredCourseIds : [];
    }
    return node.children.flatMap(collectIntensionalMemberKeys);
}

/** 内涵节点的物化成员恒等于分桶并集(每门成员用户课必落一个 status 桶) */
function collectBucketKeys(summary: CourseSetSummary): string[] {
    return Array.from(new Set([
        ...summary.completedAppliedCourseIds,
        ...summary.completedUnappliedCourseIds,
        ...summary.inProgressAppliedCourseIds,
        ...summary.inProgressUnappliedCourseIds,
        ...summary.plannedAppliedCourseIds,
        ...summary.plannedUnappliedCourseIds,
        ...summary.savedAppliedCourseIds,
        ...summary.savedUnappliedCourseIds,
    ]));
}

function patchNode(
    node: Select | CourseSet,
    nodes: Record<string, NodeFulfillment>,
): Select | CourseSet | null {
    const patch = nodes[node.id];
    if (!patch) {
        console.warn(`[fulfillmentMerge] no entry for node "${node.id}"`);
        return null;
    }

    if (node.type === "COURSE_SET") {
        const summary = patch.summary as CourseSetSummary;
        // 内涵(broad)节点:requiredCourseIds 是后端按"用户课 ∩ 谓词"物化的,随改课变化;
        // 轻量响应不带该字段,但成员恒等于分桶并集,据此回填,让所有基于
        // requiredCourseIds 的渲染路径在 patch 后保持一致
        if (node.query != null) {
            return { ...node, summary, requiredCourseIds: collectBucketKeys(summary) };
        }
        return { ...node, summary };
    }

    const children: (Select | CourseSet)[] = [];
    for (const child of node.children) {
        const patchedChild = patchNode(child, nodes);
        if (!patchedChild) return null;
        children.push(patchedChild);
    }
    return {
        ...node,
        children,
        summary: patch.summary as SelectSummary,
        fulfilledChildIds: patch.fulfilledChildIds ?? [],
    };
}
